import { useState, useEffect, useCallback } from 'react'
import { Container } from '@carouselgg/nexus/container'
import { Text } from '@carouselgg/nexus/text'
import { Card } from '@carouselgg/nexus/card'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { Badge } from '@carouselgg/nexus/badge'
import { Switch } from '@carouselgg/nexus/switch'
import { apiFetch } from '@carouselgg/auth/client'
import styles from './NotificationSettingsPage.module.css'

interface NotificationPref {
  type: string
  label: string
  description: string
  category: string
  enabled: boolean
}

const CATEGORY_LABELS: Record<string, string> = {
  organization: 'Organization',
  team: 'Team',
  match: 'Match',
  season: 'Season',
  league: 'League',
  payment: 'Payment',
  university: 'University',
  scouting: 'Scouting',
}

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  organization: 'Announcements and updates from your organizations',
  team: 'Team invitations and roster changes',
  match: 'Match scheduling, results, and changes',
  season: 'Season registration and status updates',
  league: 'League rule changes and updates',
  payment: 'Payment reminders and billing',
  university: 'Academic eligibility and university updates',
  scouting: 'Scouting interest and recruiting activity',
}

export function NotificationSettingsPage() {
  const [prefs, setPrefs] = useState<NotificationPref[]>([])
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState<string | null>(null)
  const [emailSubscribed, setEmailSubscribed] = useState<boolean | null>(null)
  const [emailToggling, setEmailToggling] = useState(false)

  useEffect(() => {
    apiFetch<NotificationPref[]>('/notifications/preferences')
      .then(setPrefs)
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    apiFetch<{ subscribed: boolean; email: string }>('/newsletter/subscription')
      .then((data) => setEmailSubscribed(data.subscribed))
      .catch(() => undefined)
  }, [])

  const handleToggleEmail = useCallback(async (subscribe: boolean) => {
    setEmailToggling(true)
    try {
      await apiFetch('/newsletter/subscription', {
        method: 'PUT',
        body: JSON.stringify({ subscribe }),
      })
      setEmailSubscribed(subscribe)
    } catch {
      // leave state unchanged on failure
    } finally {
      setEmailToggling(false)
    }
  }, [])

  const handleToggle = useCallback(
    async (type: string, enabled: boolean) => {
      setToggling(type)
      try {
        await apiFetch(`/notifications/preferences/${type}`, {
          method: 'PUT',
          body: JSON.stringify({ enabled }),
        })
        setPrefs((prev) =>
          prev.map((p) => (p.type === type ? { ...p, enabled } : p)),
        )
      } catch {
        // revert on failure handled by not updating state
      } finally {
        setToggling(null)
      }
    },
    [],
  )

  const grouped = prefs.reduce<Record<string, NotificationPref[]>>(
    (acc, p) => {
      if (!acc[p.category]) acc[p.category] = []
      acc[p.category].push(p)
      return acc
    },
    {},
  )

  return (
    <Container size="md">
      <div className={styles.page}>
        <div className={styles.header}>
          <span className={styles.eyebrow}>Your account</span>
          <DisplayHeading as="h1" size="5xl" className={styles.title}>Notifications</DisplayHeading>
          <Text muted size="sm">
            Choose which notifications you want to receive.
          </Text>
        </div>

        <Card
          header={
            <div className={styles.categoryHeader}>
              <Text as="h2" size="lg" weight="semibold">
                Email
              </Text>
              <Text size="xs" muted>
                Updates delivered to your inbox
              </Text>
            </div>
          }
        >
          <div className={styles.prefsList}>
            <div className={styles.prefRow}>
              <div className={styles.prefInfo}>
                <div className={styles.prefTitle}>
                  <Text size="sm" weight="medium">
                    Carousel news &amp; blog
                  </Text>
                  <Badge
                    variant={emailSubscribed ? 'success' : 'default'}
                    size="sm"
                  >
                    {emailSubscribed ? 'On' : 'Off'}
                  </Badge>
                </div>
                <Text size="xs" muted>
                  Get an email when we publish new posts and announcements.
                </Text>
              </div>
              <div className={styles.prefAction}>
                <Switch
                  checked={emailSubscribed ?? false}
                  disabled={emailSubscribed === null || emailToggling}
                  onChange={() => handleToggleEmail(!emailSubscribed)}
                  aria-label="Toggle Carousel news and blog emails"
                />
              </div>
            </div>
          </div>
        </Card>

        {loading ? (
          <Card>
            <div className={styles.loading}>
              <Text muted>Loading preferences...</Text>
            </div>
          </Card>
        ) : prefs.length === 0 ? (
          <Card>
            <div className={styles.loading}>
              <Text muted>No notification types available.</Text>
            </div>
          </Card>
        ) : (
          Object.entries(grouped).map(([category, items]) => (
            <Card
              key={category}
              header={
                <div className={styles.categoryHeader}>
                  <Text as="h2" size="lg" weight="semibold">
                    {CATEGORY_LABELS[category] ?? category}
                  </Text>
                  {CATEGORY_DESCRIPTIONS[category] && (
                    <Text size="xs" muted>
                      {CATEGORY_DESCRIPTIONS[category]}
                    </Text>
                  )}
                </div>
              }
            >
              <div className={styles.prefsList}>
                {items.map((pref) => (
                  <div key={pref.type} className={styles.prefRow}>
                    <div className={styles.prefInfo}>
                      <div className={styles.prefTitle}>
                        <Text size="sm" weight="medium">
                          {pref.label}
                        </Text>
                        <Badge
                          variant={pref.enabled ? 'success' : 'default'}
                          size="sm"
                        >
                          {pref.enabled ? 'On' : 'Off'}
                        </Badge>
                      </div>
                      <Text size="xs" muted>
                        {pref.description}
                      </Text>
                    </div>
                    <div className={styles.prefAction}>
                      <Switch
                        checked={pref.enabled}
                        disabled={toggling === pref.type}
                        onChange={() => handleToggle(pref.type, !pref.enabled)}
                        aria-label={`Toggle ${pref.label}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))
        )}
      </div>
    </Container>
  )
}
